/**
 * MJCF for up to 6 people plus a floor. Y-up metres, de Leva masses, ball joints
 * on the canonical ids, trunk freejoint. No wasm.
 */
import { FRICTION_MU, SEGMENTS, type SegmentDef } from "./constants.ts";
import { resolveEnd } from "./estimator.ts";
import { matToQuat, relativeXmat, rotateXmatT } from "./mujocoMap.ts";
import { len, sub } from "./vec.ts";
import type { JointId, PointMap, SegmentId, Vec3 } from "./types.ts";

export const MUJOCO_MAX_PEOPLE = 6;
/** Sphere radius (m). A landmark on the floor puts this geom through the plane. */
export const CONTACT_SPHERE_R = 0.02;
/** Rebuild when a parent-frame offset moves by more than this. */
export const LENGTH_QUANT_M = 0.005;

const CHILDREN = new Map<SegmentId | null, SegmentDef[]>();
for (const seg of SEGMENTS) {
  const list = CHILDREN.get(seg.parent) ?? [];
  list.push(seg);
  CHILDREN.set(seg.parent, list);
}

export interface MujocoPersonSpec {
  id: number;
  massKg: number;
  heightM: number;
  points: PointMap;
}

export interface SegmentPose {
  segment: SegmentId;
  joint: JointId | null;
  parent: SegmentId | null;
  origin: Vec3;
  distal: Vec3;
  length: number;
  /** Row-major body orientation; local +Y points proximal → distal. */
  R: number[];
}

export interface JointDesc {
  name: string;
  kind: "free" | "ball";
  jointId: JointId | null;
  segment: SegmentId;
}

export interface GeomDesc {
  name: string;
  jointId: JointId;
  segment: SegmentId;
}

export interface BodyDesc {
  name: string;
  segment: SegmentId;
  jointId: JointId | null;
}

export interface BuiltPerson {
  id: number;
  massKg: number;
  poses: SegmentPose[];
  joints: JointDesc[];
  geoms: GeomDesc[];
  bodies: BodyDesc[];
}

export interface MjcfBuild {
  xml: string;
  people: BuiltPerson[];
}

/** Upright pose scaled to `heightM`. Feet sit just above the floor (y = 0.04·H). */
export function standingPoints(heightM: number): PointMap {
  const h = heightM;
  const p = (x: number, y: number, z: number): Vec3 => [x * h, y * h, z * h];
  return {
    nose: p(0, 0.93, 0.02),
    left_shoulder: p(0.11, 0.82, 0),
    right_shoulder: p(-0.11, 0.82, 0),
    left_elbow: p(0.12, 0.66, 0),
    right_elbow: p(-0.12, 0.66, 0),
    left_wrist: p(0.13, 0.51, 0),
    right_wrist: p(-0.13, 0.51, 0),
    left_index: p(0.13, 0.47, 0),
    right_index: p(-0.13, 0.47, 0),
    left_hip: p(0.06, 0.53, 0),
    right_hip: p(-0.06, 0.53, 0),
    left_knee: p(0.06, 0.29, 0),
    right_knee: p(-0.06, 0.29, 0),
    left_ankle: p(0.06, 0.05, 0),
    right_ankle: p(-0.06, 0.05, 0),
    left_heel: p(0.06, 0.04, -0.03),
    right_heel: p(-0.06, 0.04, -0.03),
    left_foot_index: p(0.06, 0.04, 0.08),
    right_foot_index: p(-0.06, 0.04, 0.08),
  };
}

export function buildHumanMjcf(people: MujocoPersonSpec[], floorY = 0): string {
  return compileHumans(people, floorY).xml;
}

export function compileHumans(people: MujocoPersonSpec[], floorY = 0): MjcfBuild {
  if (people.length > MUJOCO_MAX_PEOPLE) {
    throw new Error(`mujoco: at most ${MUJOCO_MAX_PEOPLE} people`);
  }
  const built: BuiltPerson[] = [];
  for (const spec of people) {
    const person = buildPerson(spec);
    if (person) built.push(person);
  }
  const bodies = built.map((person) => emitPerson(person)).join("\n");
  const mu = FRICTION_MU.toFixed(1);
  const xml = [
    "<mujoco>",
    '  <compiler angle="degree" autolimits="true"/>',
    `  <option gravity="0 -9.81 0" timestep="0.01">`,
    '    <flag filterparent="enable"/>',
    "  </option>",
    "  <default>",
    `    <geom density="0" friction="${mu} 0.005 0.0001" condim="3" contype="1" conaffinity="1"/>`,
    "  </default>",
    "  <worldbody>",
    `    <geom name="floor" type="plane" size="10 10 0.05" pos="0 ${num(floorY)} 0" euler="-90 0 0" friction="${mu} 0.005 0.0001"/>`,
    bodies,
    "  </worldbody>",
    "</mujoco>",
    "",
  ].join("\n");
  return { xml, people: built };
}

/** Quantised lengths / parent offsets. Unchanged → the compiled model can be reused. */
export function modelKey(people: BuiltPerson[], floorY: number): string {
  const parts: string[] = [quant(floorY).toFixed(4)];
  for (const person of people) {
    parts.push(String(person.id), person.massKg.toFixed(4));
    const byId = new Map(person.poses.map((pose) => [pose.segment, pose]));
    for (const pose of person.poses) {
      const parent = pose.parent ? byId.get(pose.parent) : undefined;
      const local = parent ? rotateXmatT(sub(pose.origin, parent.origin), parent.R) : pose.origin;
      parts.push(
        pose.segment,
        quant(pose.length).toFixed(4),
        quant(local[0]).toFixed(4),
        quant(local[1]).toFixed(4),
        quant(local[2]).toFixed(4),
      );
    }
  }
  return parts.join("|");
}

export function buildPerson(spec: MujocoPersonSpec): BuiltPerson | null {
  const poses = segmentPoses(spec.points);
  if (!poses) return null;
  const id = spec.id;
  const joints: JointDesc[] = [];
  const geoms: GeomDesc[] = [];
  const bodies: BodyDesc[] = [];
  for (const pose of poses) {
    const bodyName = `p${id}_${pose.segment}`;
    bodies.push({ name: bodyName, segment: pose.segment, jointId: pose.joint });
    if (pose.joint == null) {
      joints.push({ name: `p${id}_root`, kind: "free", jointId: null, segment: pose.segment });
    } else {
      joints.push({ name: `p${id}_${pose.joint}`, kind: "ball", jointId: pose.joint, segment: pose.segment });
    }
    const jointId = pose.joint ?? "hip_l";
    geoms.push({ name: `${bodyName}_seg`, jointId, segment: pose.segment });
    if (pose.segment === "foot_l" || pose.segment === "foot_r" || pose.segment === "hand_l" || pose.segment === "hand_r") {
      geoms.push({ name: `${bodyName}_tip`, jointId, segment: pose.segment });
    }
  }
  return { id, massKg: spec.massKg, poses, joints, geoms, bodies };
}

function segmentPoses(points: PointMap): SegmentPose[] | null {
  const trunk = CHILDREN.get(null)?.[0];
  if (!trunk) return null;
  const poses: SegmentPose[] = [];
  const byId = new Map<SegmentId, SegmentPose>();
  const walk = (def: SegmentDef, parent: SegmentPose | null): void => {
    const proximal = resolveEnd(points, def.proximal);
    const distal = resolveEnd(points, def.distal);
    if (!proximal || !distal) return;
    const hint = parent ? xAxis(parent.R) : hipHint(points);
    const framed = frameY(proximal, distal, hint);
    if (!framed) return;
    const pose: SegmentPose = {
      segment: def.id,
      joint: def.joint,
      parent: def.parent,
      origin: proximal,
      distal,
      length: framed.length,
      R: framed.R,
    };
    poses.push(pose);
    byId.set(def.id, pose);
    for (const child of CHILDREN.get(def.id) ?? []) walk(child, pose);
  };
  walk(trunk, null);
  if (!byId.has("trunk")) return null;
  return poses;
}

function hipHint(points: PointMap): Vec3 {
  const left = points.left_hip;
  const right = points.right_hip;
  if (left && right) {
    const lateral = sub(left, right);
    if (len(lateral) > 1e-4) return lateral;
  }
  return [1, 0, 0];
}

function xAxis(R: readonly number[]): Vec3 {
  return [R[0]!, R[3]!, R[6]!];
}

function frameY(origin: Vec3, target: Vec3, hintX: Vec3): { R: number[]; length: number } | null {
  const delta = sub(target, origin);
  const length = len(delta);
  if (!(length > 1e-4)) return null;
  const y: Vec3 = [delta[0] / length, delta[1] / length, delta[2] / length];
  let x = reject(hintX, y);
  if (len(x) < 1e-4) {
    const alt: Vec3 = Math.abs(y[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0];
    x = reject(alt, y);
  }
  const xn = len(x);
  if (!(xn > 1e-8)) return null;
  x = [x[0] / xn, x[1] / xn, x[2] / xn];
  const z: Vec3 = [x[1] * y[2] - x[2] * y[1], x[2] * y[0] - x[0] * y[2], x[0] * y[1] - x[1] * y[0]];
  return {
    length,
    R: [x[0], y[0], z[0], x[1], y[1], z[1], x[2], y[2], z[2]],
  };
}

function reject(v: Vec3, axis: Vec3): Vec3 {
  const d = v[0] * axis[0] + v[1] * axis[1] + v[2] * axis[2];
  return [v[0] - axis[0] * d, v[1] - axis[1] * d, v[2] - axis[2] * d];
}

function emitPerson(person: BuiltPerson): string {
  const poseOf = new Map(person.poses.map((pose) => [pose.segment, pose]));
  const trunk = poseOf.get("trunk");
  if (!trunk) return "";
  return emitBody(person, trunk, null, poseOf, "    ");
}

function emitBody(
  person: BuiltPerson,
  pose: SegmentPose,
  parent: SegmentPose | null,
  poseOf: Map<SegmentId, SegmentPose>,
  indent: string,
): string {
  const def = SEGMENTS.find((seg) => seg.id === pose.segment);
  if (!def) return "";
  const local = parent ? rotateXmatT(sub(pose.origin, parent.origin), parent.R) : ([0, 0, 0] as Vec3);
  const mass = Math.max(1e-4, def.massFraction * person.massKg);
  const iTrans = Math.max(1e-6, mass * (def.gyrationRatio * pose.length) ** 2);
  const iLong = Math.max(1e-6, iTrans * 0.25);
  const com = def.comRatio * pose.length;
  const lines = [
    `${indent}<body name="p${person.id}_${pose.segment}" pos="${num(local[0])} ${num(local[1])} ${num(local[2])}">`,
  ];
  if (pose.joint == null) {
    lines.push(`${indent}  <freejoint name="p${person.id}_root"/>`);
  } else {
    lines.push(`${indent}  <joint name="p${person.id}_${pose.joint}" type="ball"/>`);
  }
  lines.push(
    `${indent}  <inertial pos="0 ${num(com)} 0" mass="${num(mass)}" diaginertia="${num(iTrans)} ${num(iLong)} ${num(iTrans)}"/>`,
  );
  lines.push(emitSegGeom(person.id, pose, indent + "  "));
  if (isTip(pose.segment)) {
    lines.push(
      `${indent}  <geom name="p${person.id}_${pose.segment}_tip" type="sphere" pos="0 ${num(pose.length)} 0" size="${num(CONTACT_SPHERE_R)}"/>`,
    );
  }
  for (const childDef of CHILDREN.get(pose.segment) ?? []) {
    const child = poseOf.get(childDef.id);
    if (!child) continue;
    lines.push(emitBody(person, child, pose, poseOf, indent + "  "));
  }
  lines.push(`${indent}</body>`);
  return lines.join("\n");
}

function emitSegGeom(personId: number, pose: SegmentPose, indent: string): string {
  const name = `p${personId}_${pose.segment}_seg`;
  const radius = Math.min(0.045, Math.max(0.012, pose.length * 0.12));
  if (pose.length <= radius * 2.5) {
    return `${indent}<geom name="${name}" type="sphere" pos="0 ${num(pose.length * 0.5)} 0" size="${num(Math.max(0.01, pose.length * 0.3))}"/>`;
  }
  const inset = radius * 0.5;
  return `${indent}<geom name="${name}" type="capsule" fromto="0 ${num(inset)} 0 0 ${num(pose.length - inset)} 0" size="${num(radius)}"/>`;
}

function isTip(segment: SegmentId): boolean {
  return segment === "foot_l" || segment === "foot_r" || segment === "hand_l" || segment === "hand_r";
}

function quant(x: number): number {
  const s = 1 / LENGTH_QUANT_M;
  return Math.round(x * s) / s;
}

function num(x: number): string {
  if (!Number.isFinite(x)) return "0";
  const rounded = Math.round(x * 1e6) / 1e6;
  return Object.is(rounded, -0) ? "0" : String(rounded);
}

/** Relative ball/free quaternion for the current pose of one built joint. */
export function jointQuaternion(pose: SegmentPose, parent: SegmentPose | null): [number, number, number, number] {
  return matToQuat(parent ? relativeXmat(parent.R, pose.R) : pose.R);
}
